import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { useAuthContext } from './useAuthContext';

/**
 * Hook to fetch product ratings
 */
export const useProductRatings = (productId) => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRatings = useCallback(async () => {
    if (!productId) {
      setRatings([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching product ratings for product:', productId);

      const { data, error: fetchError } = await supabase
        .rpc("get_product_ratings", { p_product_id: productId });

      if (fetchError) {
        throw fetchError;
      }

      console.log('Product ratings fetched:', data);
      setRatings(data || []);
    } catch (err) {
      console.error('Error fetching product ratings:', err);
      setError(err.message);
      setRatings([]);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  return { ratings, loading, error, refetch: fetchRatings };
};

/**
 * Hook to check if user can rate a product/variant
 * Returns order items that match the product/variant that the user has purchased
 */
export const useCanRateProduct = (productId, variantId = null) => {
  const { session } = useAuthContext();
  const [canRate, setCanRate] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!productId || !session) {
      setCanRate(false);
      setOrderItems([]);
      setLoading(false);
      return;
    }

    const checkCanRate = async () => {
      try {
        setLoading(true);
        console.log('Checking if user can rate product:', productId, 'variant:', variantId);

        // Build query to find order items for this product
        let query = supabase
          .from('order_items')
          .select(`
            *,
            order:orders!inner(
              id,
              profile_id,
              status,
              created_at
            )
          `)
          .eq('order.profile_id', session.user.id)
          .eq('product_id', productId);

        // If variant is specified, filter by variant_id
        if (variantId) {
          query = query.eq('variant_id', variantId);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) {
          throw fetchError;
        }

        // Filter to only include delivered orders
        const eligibleItems = (data || []).filter(item => {
          const order = item.order;
          return order && order.status === 'delivered';
        });

        console.log('Eligible order items for rating:', eligibleItems);
        setOrderItems(eligibleItems);
        setCanRate(eligibleItems.length > 0);
      } catch (err) {
        console.error('Error checking if user can rate:', err);
        setError(err.message);
        setCanRate(false);
        setOrderItems([]);
      } finally {
        setLoading(false);
      }
    };

    checkCanRate();
  }, [productId, variantId, session]);

  return { canRate, orderItems, loading, error };
};

/**
 * Hook to fetch existing rating for an order item
 */
export const useOrderItemRating = (orderItemId) => {
  const { session } = useAuthContext();
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderItemId || !session) {
      setRating(null);
      setLoading(false);
      return;
    }

    const fetchRating = async () => {
      try {
        setLoading(true);
        console.log('Fetching rating for order item:', orderItemId);

        const { data, error: fetchError } = await supabase
          .from('product_ratings')
          .select('*')
          .eq('order_item_id', orderItemId)
          .eq('created_by', session.user.id)
          .maybeSingle();

        if (fetchError) {
          throw fetchError;
        }

        console.log('Existing rating found:', data);
        setRating(data);
      } catch (err) {
        console.error('Error fetching order item rating:', err);
        setError(err.message);
        setRating(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRating();
  }, [orderItemId, session]);

  return { rating, loading, error };
};

